import React, { useEffect, useRef } from 'react';
import { LogOut, X } from 'lucide-react';

interface LogoutConfirmModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
  /** هنگام خروج واقعی */
  isLoading?: boolean;
}

const LogoutConfirmModal: React.FC<LogoutConfirmModalProps> = ({
  open,
  onCancel,
  onConfirm,
  isLoading = false,
}) => {
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => confirmBtnRef.current?.focus(), 100);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel, isLoading]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-confirm-title"
    >
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm animate-backdrop-in"
        onClick={isLoading ? undefined : onCancel}
        aria-hidden
      />
      <div
        className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl border border-amber-100/80 animate-modal-in"
        dir="rtl"
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="absolute top-3 left-3 p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-40"
          aria-label="بستن"
        >
          <X size={18} />
        </button>
        <div className="flex flex-col items-center text-center pt-1">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
            <LogOut size={28} strokeWidth={2} />
          </div>
          <h2
            id="logout-confirm-title"
            className="text-lg font-bold text-slate-800"
          >
            خروج از سامانه؟
          </h2>
          <p className="mt-2 text-sm text-slate-500 leading-relaxed">
            نشست کاری شما بسته می‌شود؛ برای ادامه باید دوباره وارد شوید.
          </p>
        </div>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="w-full sm:w-auto rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            انصراف
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={() => void onConfirm()}
            disabled={isLoading}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
          >
            {isLoading ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-white" />
                در حال خروج...
              </>
            ) : (
              <>
                <LogOut size={16} />
                بله، خروج
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogoutConfirmModal;
